import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import VerifiedBadge from "../components/VerifiedBadge";
import StarRating from "../components/StarRating";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, ExternalLink, MessageSquare, MessageCircle, Send, Mail, Copy } from "lucide-react";

export default function FreelancerProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [msg, setMsg] = useState("");
  const [ratings, setRatings] = useState([]);

  useEffect(() => {
    api.get(`/freelancers/${username}`).then(r => {
      setF(r.data);
      if (r.data?.user_id) {
        api.get(`/ratings/${r.data.user_id}`).then(rr => setRatings(rr.data)).catch(() => {});
      }
    }).catch(() => setF(null));
  }, [username]);

  const hire = async () => {
    if (!user) return nav("/login");
    try {
      await api.post(`/freelancers/${username}/hire`, { message: msg });
      toast.success("Request sent!");
      setMsg("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!f) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const phone = f.phone;

  const openWhatsApp = () => {
    if (phone) {
      window.open(`https://wa.me/${phone.replace(/\D/g, "")}`, "_blank");
    } else {
      toast.error("This freelancer hasn't added a phone number");
    }
  };

  const openTelegram = () => {
    if (phone) {
      window.open(`https://t.me/+${phone.replace(/\D/g, "")}`, "_blank");
    } else {
      toast.error("This freelancer hasn't added a phone number");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8" data-testid="freelancer-profile-page">
      <div className="flex items-start gap-4 flex-wrap">
        {f.avatar
          ? <img src={f.avatar} alt="" className="w-20 h-20 rounded-full object-cover" />
          : <div className="w-20 h-20 rounded-full bg-slate-100 grid place-items-center font-display text-3xl">{(f.name || "?")[0]}</div>}
        <div className="flex-1">
          <h1 className="font-display text-3xl tracking-tighter flex items-center gap-2 flex-wrap">
            {f.name}
            {f.verified && <VerifiedBadge size={18} showLabel />}
            {f.availability && <CheckCircle2 className="inline w-5 h-5 text-emerald-600" />}
          </h1>
          <p className="text-muted-foreground">@{f.username}</p>
          <span className="label-eyebrow text-muted-foreground">{f.experience_level || "Freelancer"}</span>
          {f.rating && (
            <div className="mt-1">
              <StarRating rating={f.rating} count={f.rating_count} size="sm" />
            </div>
          )}
        </div>
      </div>

      {/* Contact buttons */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={openWhatsApp} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </button>
        <button onClick={openTelegram} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
          <Send className="w-4 h-4" /> Telegram
        </button>
        <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); }} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
          <Copy className="w-4 h-4" /> Copy link
        </button>
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <section>
            <h2 className="font-display text-xl mb-2">About</h2>
            <p className="text-muted-foreground whitespace-pre-line">{f.about || "—"}</p>
          </section>
          <section>
            <h2 className="font-display text-xl mb-2">Specializations</h2>
            <div className="flex flex-wrap gap-2">
              {(f.specializations || []).map(s => <span key={s} className="text-xs px-2 py-1 bg-slate-100 rounded-sm font-mono">{s}</span>)}
            </div>
          </section>
          <section>
            <h2 className="font-display text-xl mb-2">Skills</h2>
            <div className="flex flex-wrap gap-2">
              {(f.skills || []).map(s => <span key={s} className="text-xs px-2 py-1 border border-slate-300 rounded-sm">{s}</span>)}
            </div>
          </section>
          <section>
            <h2 className="font-display text-xl mb-2">Portfolio</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(f.portfolio_images || []).map((u, i) => <img key={i} src={u} alt="" className="w-full aspect-square object-cover rounded-sm border border-slate-200" />)}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(f.portfolio_links || {}).filter(([_, v]) => v).map(([k, v]) => (
                <a key={k} href={v} target="_blank" rel="noreferrer" className="btn-outline !py-2 !px-3 text-xs"><ExternalLink className="w-3 h-3" /> {k}</a>
              ))}
            </div>
          </section>

          {/* Reviews */}
          {ratings.length > 0 && (
            <section>
              <h2 className="font-display text-xl mb-3">Reviews ({ratings.length})</h2>
              <div className="space-y-3">
                {ratings.slice(0, 5).map(r => (
                  <div key={r.id} className="card-flat">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-sm">{r.rater_name}</span>
                      <StarRating rating={r.stars} size="sm" />
                      <span className="text-xs text-muted-foreground">{r.job_title}</span>
                    </div>
                    {r.comment && <p className="text-sm text-slate-600">{r.comment}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-4">
          <div className="card-flat">
            <p className="label-eyebrow text-muted-foreground">Pricing</p>
            <p className="mt-2 font-display text-2xl">{f.pricing || "On request"}</p>
            <textarea rows={3} className="input-base mt-4 !rounded-2xl" placeholder="Project brief…" value={msg} onChange={e => setMsg(e.target.value)} data-testid="hire-message" />
            <button onClick={hire} className="btn-primary w-full mt-3" data-testid="hire-btn">Hire {f.name?.split(" ")[0]}</button>
            <button onClick={() => { if (!user) return nav("/login"); nav(`/messages/${f.user_id}`); }} className="btn-tint w-full mt-2" data-testid="message-freelancer-btn">
              <MessageSquare className="w-4 h-4" /> Send message
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
